import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

export default function AuditLogs() {
  const { user: currentUser } = useAuth();
  const isPlatformOwner = currentUser?.isSuperAdmin;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    action: '',
    securityOnly: false,
    companyId: ''
  });
  const [actions, setActions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0
  });
  const [localSearch, setLocalSearch] = useState('');
  const [sortField, setSortField] = useState('CreatedAt');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  useEffect(() => {
    if (isPlatformOwner) {
      apiClient.get('/companies').then(r => {
        setCompanies(Array.isArray(r.data) ? r.data : []);
      }).catch(() => {});
    }
    fetchActions();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [pagination.offset, filters]);

  // Re-fetch action list when company changes (for platform owners)
  useEffect(() => {
    fetchActions();
  }, [filters.companyId]);

  const fetchActions = async () => {
    try {
      const params = filters.companyId ? `?companyId=${filters.companyId}` : '';
      const response = await apiClient.get(`/audit-logs/actions${params}`);
      setActions(response.data.actions || []);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== false))
      });
      const response = await apiClient.get(`/audit-logs?${params}`);
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(`/audit-logs/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export logs:', err);
      alert('Failed to export audit logs');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      offset: direction === 'next' 
        ? prev.offset + prev.limit 
        : Math.max(0, prev.offset - prev.limit)
    }));
  };

  const filteredLogs = localSearch
    ? logs.filter(log => {
        const t = localSearch.toLowerCase();
        return (
          (log.ActorUsername && log.ActorUsername.toLowerCase().includes(t)) ||
          (log.ActorEmail    && log.ActorEmail.toLowerCase().includes(t)) ||
          (log.Action        && log.Action.toLowerCase().includes(t)) ||
          (log.ChangesSummary && log.ChangesSummary.toLowerCase().includes(t))
        );
      })
    : logs;

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    let aVal = a[sortField], bVal = b[sortField];
    if (sortField === 'CreatedAt') { aVal = new Date(aVal || 0); bVal = new Date(bVal || 0); }
    else { aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase(); }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <button
          onClick={handleExport}
          className="btn btn-success"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        {/* Text search */}
        <div className="mb-3 relative">
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Search by user, email or action details…"
            className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {localSearch && (
            <button onClick={() => setLocalSearch('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {isPlatformOwner && companies.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.companyId}
                onChange={(e) => handleFilterChange('companyId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Actions</option>
              {actions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.securityOnly}
                onChange={(e) => handleFilterChange('securityOnly', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Security Critical Only</span>
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Loading audit logs...</div>
          </div>
        ) : (
          <>
            <table className="w-full table-fixed divide-y divide-gray-200">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[20%]" />
                <col className="w-[18%]" />
                <col className="w-[36%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh field="CreatedAt" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Timestamp</SortableTh>
                  <SortableTh field="Action" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Action</SortableTh>
                  <SortableTh field="ActorUsername" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>User</SortableTh>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <SortableTh field="IPAddress" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>IP Address</SortableTh>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedLogs.length > 0 ? (
                  sortedLogs.map((log) => (
                    <tr key={log.AuditEventID} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {new Date(log.CreatedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {log.WasSecurityCritical && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded whitespace-nowrap">Security</span>
                          )}
                          <span className="font-medium text-gray-900 truncate">{log.Action}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="font-medium truncate">{log.ActorUsername || 'System'}</div>
                        <div className="text-xs text-gray-500 truncate">{log.ActorEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate">
                        {log.ChangesSummary || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {log.IPAddress || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      {localSearch ? `No results matching "${localSearch}"` : 'No audit logs found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <TablePagination
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              pageSize={pagination.limit}
              total={pagination.total}
              onPageChange={(newPage) => setPagination(prev => ({ ...prev, offset: (newPage - 1) * prev.limit }))}
            />
          </>
        )}
      </div>
    </div>
  );
}
